import { Alert, Button, Input, Modal } from "@arco-design/web-react";
import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import QRCode from "react-qr-code";

import huaerArtTravelLogo from "../../../assets/brand/huaer-art-travel-logo.png";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";

type Props = {
  visible: boolean;
  shareUrl: string;
  onClose: () => void;
};

const defaultDescription = "请用微信扫描下方二维码\n查看完整行程";
const defaultSignature = "花儿专属为您定制";

/** 可下载为 PNG 的二维码分享卡片，文案编辑只在弹层内生效。 */
export function ItineraryShareQrModal({ visible, shareUrl, onClose }: Props) {
  const cardRef = useRef<HTMLElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>();
  const [title, setTitle] = useState("亲爱的老师");
  const [description, setDescription] = useState(defaultDescription);
  const [signature, setSignature] = useState(defaultSignature);

  useEffect(() => {
    if (!visible) return;
    setDownloadError(undefined);
    setTitle("亲爱的老师");
    setDescription(defaultDescription);
    setSignature(defaultSignature);
  }, [shareUrl, visible]);

  useEffect(() => {
    if (!visible) return;
    const focusTimer = window.setTimeout(() => cardRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [visible]);

  /** 只下载卡片；导出副本用普通文本保留全部换行，避免输入框被截取。 */
  const downloadCardImage = async () => {
    const card = cardRef.current;
    if (!card || downloading) return;

    setDownloading(true);
    setDownloadError(undefined);
    try {
      const canvas = await html2canvas(card, {
        backgroundColor: "#ffffff",
        scale: Math.max(2, window.devicePixelRatio || 1),
        useCORS: true,
        onclone: (document, clonedCard) => {
          clonedCard.querySelectorAll("textarea").forEach((input) => {
            const text = document.createElement("div");
            text.className = input.className.replace("arco-textarea", "");
            text.textContent = input.value;
            text.style.minHeight = input.style.minHeight;
            input.replaceWith(text);
          });
        },
      });
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((image) => image ? resolve(image) : reject(new Error("PNG 生成失败")), "image/png");
      });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = "行程分享.png";
      anchor.hidden = true;
      document.body.append(anchor);
      try { anchor.click(); }
      finally {
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
      }
    } catch {
      setDownloadError("图片下载失败，请重试");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      className="itinerary-share-qr-modal"
      visible={visible}
      title={<span className="itinerary-share-qr-dialog-title">分享二维码</span>}
      style={{ width: 320 }}
      footer={null}
      closeIcon={<ItineraryDialogCloseButton label="关闭分享二维码" />}
      maskStyle={{ zIndex: 1399 }}
      wrapStyle={{ zIndex: 1400 }}
      onCancel={onClose}
      unmountOnExit
    >
      <div className="itinerary-share-qr-workspace">
        <div className="itinerary-share-card-actions">
          <Button
            className="itinerary-share-download-image"
            type="primary"
            loading={downloading}
            disabled={downloading}
            onClick={() => void downloadCardImage()}
          >
            下载图片
          </Button>
        </div>
        {downloadError ? <Alert type="error" showIcon content={downloadError} /> : null}
        <article
          ref={cardRef}
          className="itinerary-share-card"
          aria-label="客户分享卡片"
          tabIndex={-1}
        >
          <img
            className="itinerary-share-card-logo"
            src={huaerArtTravelLogo}
            alt="花儿艺术旅行公司标识"
          />
          <Input.TextArea
            className="itinerary-share-card-text itinerary-share-card-title"
            aria-label="分享标题"
            value={title}
            maxLength={30}
            autoSize={{ minRows: 1 }}
            onChange={setTitle}
          />
          <Input.TextArea
            className="itinerary-share-card-text itinerary-share-card-description"
            aria-label="分享说明"
            value={description}
            maxLength={120}
            autoSize={{ minRows: 2 }}
            onChange={setDescription}
          />

          <div className="itinerary-share-card-qr">
            {shareUrl ? (
              <QRCode
                value={shareUrl}
                size={208}
                level="Q"
                bgColor="#ffffff"
                fgColor="#172033"
                role="img"
                aria-label="客户分享二维码"
              />
            ) : null}
          </div>
          <footer>
            <Input.TextArea
              className="itinerary-share-card-text"
              aria-label="分享署名"
              value={signature}
              maxLength={40}
              autoSize={{ minRows: 1 }}
              onChange={setSignature}
            />
          </footer>
        </article>
      </div>
    </Modal>
  );
}
